import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { specialties } from '@/mocks/homeData';

export default function SpecialtiesSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section id="especialidades" ref={ref} className="relative py-24 md:py-32 lg:py-40 overflow-hidden">
      {/* Lilás gradient background 30% */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary-100/30 via-primary-50/20 to-background-50 pointer-events-none" />
      {/* Decorative blobs */}
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full bg-accent-50/30 translate-y-1/2 -translate-x-1/3 blur-3xl pointer-events-none" />
      <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-primary-100/30 -translate-y-1/3 translate-x-1/4 blur-3xl pointer-events-none" />

      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        {/* Header */}
        <div className="text-center max-w-[600px] mx-auto mb-16 md:mb-20">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-xs tracking-[0.2em] uppercase text-primary-400 font-label"
          >
            O que fazemos
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground-950 mt-4 leading-[1.1]"
          >
            Cuidado integral<br />
            <span className="italic font-light">para cada fase.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-4 text-foreground-500 text-sm leading-relaxed"
          >
            Da adolescência à maturidade, oferecemos um portfólio completo de serviços pensados para a saúde e o bem-estar da mulher.
          </motion.p>
        </div>

        {/* Cards Grid with Tilt */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {specialties.map((spec, index) => (
            <TiltCard key={spec.id} spec={spec} index={index} isInView={isInView} />
          ))}
        </div>
      </div>
    </section>
  );
}

function TiltCard({ spec, index, isInView }: {
  spec: typeof specialties[0];
  index: number;
  isInView: boolean;
}) {
  const cardRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    cardRef.current.style.transform = `perspective(1000px) rotateY(${x * 4}deg) rotateX(${-y * 4}deg) translateZ(0)`;
  };

  const handleMouseLeave = () => {
    if (!cardRef.current) return;
    cardRef.current.style.transform = 'perspective(1000px) rotateY(0deg) rotateX(0deg) translateZ(0)';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: 0.1 + index * 0.08, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <div
        ref={cardRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className="group relative bg-background-50 rounded-lg overflow-hidden border border-background-200/70 transition-shadow duration-500 hover:shadow-[0_25px_70px_-25px_rgba(0,0,0,0.07)]"
        style={{ transition: 'transform 0.4s cubic-bezier(0.23, 1, 0.32, 1), box-shadow 0.5s ease' }}
      >
        {/* Image */}
        <div className="relative h-52 overflow-hidden">
          <img
            src={spec.image}
            alt={spec.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent" />
          {/* Shine overlay on hover */}
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-gradient-to-tr from-white/0 via-white/10 to-white/0 pointer-events-none translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
        </div>

        {/* Content */}
        <div className="p-6 md:p-7">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 flex-shrink-0 transition-colors duration-400 group-hover:bg-primary-100 group-hover:text-primary-600">
              <i className={`${spec.icon} text-lg`}></i>
            </div>
            <div>
              <h3 className="font-heading text-xl md:text-2xl text-foreground-950 leading-tight whitespace-nowrap">
                {spec.title}
              </h3>
              <p className="mt-2 text-foreground-500 text-xs md:text-sm leading-relaxed">
                {spec.description}
              </p>
              <span className="inline-flex items-center gap-1 mt-4 text-xs tracking-[0.06em] uppercase text-primary-500 font-label group-hover:gap-2 transition-all duration-300 whitespace-nowrap">
                Saiba mais
                <i className="ri-arrow-right-line text-xs transition-transform duration-300 group-hover:translate-x-1"></i>
              </span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}